import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  ArrowLeft,
  Target,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import logger from '../utils/logger';
import { post } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';

const ExamResults = () => {  
  const { id: examId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  // Image zoom and pan states for modal
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef(null);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
  };

  const closeModal = () => {
    setSelectedImage(null);
    setZoomScale(1);
    setPan({ x: 0, y: 0 });
    setIsFullscreen(false);
  };

  const handleMouseDown = (e) => {
    if (zoomScale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomScale > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomScale > 1 && e.touches.length === 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && zoomScale > 1 && e.touches.length === 1) {
      setPan({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (zoomScale > 1) {
      setZoomScale(1);
      setPan({ x: 0, y: 0 });
    } else {
      setZoomScale(2);
      setPan({ x: 0, y: 0 });
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Get results and exam data from location state
  const locationState = location.state || {};
  const { examData, answers, score, totalQuestions, correctAnswers, timeTaken, questions } = locationState;



  // Sync isFullscreen state with browser fullscreen events
  useEffect(() => {
    const onFsChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isFull);
      if (!isFull && selectedImage) {
        setSelectedImage(null);
        setZoomScale(1);
        setPan({ x: 0, y: 0 });
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [selectedImage]);

  useEffect(() => {
  
    
    if (examData === null || examData === undefined || answers === null || answers === undefined || score === null || score === undefined || totalQuestions === null || totalQuestions === undefined || correctAnswers === null || correctAnswers === undefined || timeTaken === null || timeTaken === undefined || questions === null || questions === undefined) {
  
      // navigate('/exams');
      return;
    }
    
    // Submit exam results to API
    const submitExamResults = async () => {
      try {
       
        const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
        const lastAttemptDate = new Date().toISOString();
        
        // Debug: Log the original answers data
        logger.log('🔍 Original answers data:', answers);
        logger.log('🔍 Answers type:', typeof answers);
        
        // Transform answers object to array format with questionNumber, userAnswer, and isCorrect
        const transformedAnswers = [];
        
        // Process all questions from the exam, not just answered ones
        if (questions && Array.isArray(questions)) {
          questions.forEach((question, index) => {
            const questionNumber = index + 1;
            const questionId = question.id || question.questionId;
            
            // Check if user answered this question
            const userAnswer = answers && answers[questionId];
           
            const finalUserAnswer = (userAnswer === null || userAnswer === undefined || userAnswer === '' || 
                                   (Array.isArray(userAnswer) && userAnswer.length === 0)) 
              ? 'did not answer' 
              : userAnswer;
            
            transformedAnswers.push({
              questionNumber: questionNumber,
              userAnswer: finalUserAnswer,
              // We'll need to determine this from the correct answers
            });
          });
        }
        
        logger.log('🔍 Transformed answers array:', transformedAnswers);

        const payload = {
          examId: location.state?.examData?.id,
          score: correctAnswers,
          percentage: percentage,
          lastAttemptDate: lastAttemptDate,
          answers: transformedAnswers
        };
        
        logger.log('🔍 Final payload:', payload);
        
        
        
        const response = await post(getFullUrl('exam-result'), payload);
        
        if (response.ok) {
          logger.log('✅ Exam results submitted successfully');
        } else {
          logger.error('❌ Failed to submit exam results:', response.status);
        }
      } catch (error) {
        logger.error('❌ Error submitting exam results:', error);
      }
    };
    
    submitExamResults();
    
    logger.log('✅ All required data present, showing results');
    // No need to fetch API data - we have everything from location state
    setLoading(false);
  }, [examData, answers, score, totalQuestions, correctAnswers, timeTaken, questions, navigate, location.state]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mb-2">Time taken: {formatTime(timeTaken)}</p>
        </div>
      </div>
    );
  }

  if (examData === null || examData === undefined || answers === null || answers === undefined || score === null || score === undefined || totalQuestions === null || totalQuestions === undefined || correctAnswers === null || correctAnswers === undefined || timeTaken === null || timeTaken === undefined) {
    logger.error('❌ SECOND VALIDATION FAILED - Missing required data:', {
      examData: examData,
      examDataType: typeof examData,
      answers: answers,
      answersType: typeof answers,
      score: score,
      scoreType: typeof score,
      totalQuestions: totalQuestions,
      totalQuestionsType: typeof totalQuestions,
      correctAnswers: correctAnswers,
      correctAnswersType: typeof correctAnswers,
      timeTaken: timeTaken,
      timeTakenType: typeof timeTaken
    });
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Results Not Found</h2>
          <p className="text-gray-600 mb-6">Unable to load exam results.</p>
          <button
            onClick={() => navigate('/exams')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Exams
          </button>
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreMessage = (score) => {
    if (score >= 90) return 'Outstanding! 🌟';
    if (score >= 80) return 'Excellent! 🎉';
    if (score >= 70) return 'Good job! 👍';
    if (score >= 60) return 'Keep practicing! 💪';
    return 'Keep studying! 📚';
  };

  // Process questions with user answers - use actual questions from location state
  const processedQuestions = Array.isArray(questions) ? questions.map(question => {
    const userAnswer = answers[question.questionId];
    let correctAnswer = question.correctAnswers || question.correctAnswer;
    
    // Convert correctAnswer to array, handling comma-separated indices like "1,2"
    let correctAnswersArray;
    if (Array.isArray(correctAnswer)) {
      correctAnswersArray = correctAnswer;
    } else if (typeof correctAnswer === 'string' && correctAnswer.includes(',')) {
      correctAnswersArray = correctAnswer.split(',');
    } else {
      correctAnswersArray = [correctAnswer];
    }
    
    // Determine if answer is correct based on question type
    let isCorrect = false;
    if (question.questionType === 'LONG_WRITING') {
      // For long writing, always consider correct if any answer provided
      isCorrect = userAnswer && userAnswer.trim().length > 0;
    } else if (question.questionType === 'MULTI_SELECT') {
      // For multi-select, check if arrays match (convert indices to letters for comparison)
      const userAnswersArray = Array.isArray(userAnswer) ? userAnswer.map(String) : [];
      const correctAnswersAsLetters = correctAnswersArray.map(ans => {
        const index = parseInt(ans);
        return isNaN(index) ? String(ans) : String.fromCharCode(65 + index);
      });
      isCorrect = userAnswersArray.length === correctAnswersAsLetters.length && 
                 userAnswersArray.every(ans => correctAnswersAsLetters.includes(ans));
    } else if (question.questionType === 'SHORT_ANSWER') {
      // For short answer, compare case-insensitive with type conversion
      const userStr = String(userAnswer).trim().toLowerCase();
      const correctStr = String(correctAnswer).trim().toLowerCase();
      isCorrect = userStr === correctStr || Number(userAnswer) === Number(correctAnswer);
    } else {
      // For MCQ, compare selected option
      if (userAnswer !== undefined && userAnswer !== null) {
        const selectedOption = question.options[userAnswer];
        const selectedOptionId = selectedOption?.id || '';
        const correctIndex = parseInt(correctAnswer);
        isCorrect = selectedOptionId === correctAnswer || 
                    selectedOption === correctAnswer ||
                    (typeof correctAnswer === 'string' && selectedOption?.text === correctAnswer) ||
                    (!isNaN(correctIndex) && Number(userAnswer) === correctIndex);
      }
    }
    
    return {
      ...question,
      userAnswer,
      isCorrect,
      correctAnswer: correctAnswersArray // Use array for consistency
    };
  }) : [];

  // Calculate recalculated values for debugging
  const recalculatedCorrect = processedQuestions.filter(q => q.isCorrect).length;
  const recalculatedScore = totalQuestions > 0 ? Math.round((recalculatedCorrect / totalQuestions) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/exams')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Exams
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Exam Results</h1>
        <p className="text-gray-600 mt-2">{examData.title}</p>
      </div>

      {/* Score Overview */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <div className="text-center">
          <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${getScoreBgColor(score)} mb-4`}>
            <span className={`text-3xl font-bold ${getScoreColor(score)}`}>
              {score}%
            </span>
          </div>
          <h2 className={`text-2xl font-bold mb-2 ${getScoreColor(score)}`}>
            {getScoreMessage(score)}
          </h2>
          <p className="text-gray-600">
            You got {correctAnswers} out of {totalQuestions} questions correct
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">Total Questions</span>
            <Target className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{totalQuestions}</p>
          <p className="text-sm text-gray-600">Questions answered</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">Correct Answers</span>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-600">{correctAnswers}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600">Time Taken</span>
            <Clock className="h-5 w-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatTime(timeTaken)}</p>
        </div>
      </div>

      {/* Detailed Results */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Question Review</h3>
        
        <div className="space-y-6">
          {processedQuestions.map((question, index) => (
            <div key={question.questionId} className="border-b border-gray-200 pb-6 last:border-b-0">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="text-sm font-medium text-gray-500 mr-3">
                      Question {index + 1}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 mr-2">
                      {question.questionType}
                    </span>
                    {question.isCorrect ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Correct
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-3 w-3 mr-1" />
                        Incorrect
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 font-medium mb-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: question.question }} />
                  
                  {/* Disclaimer for LONG_WRITING - shown below question */}
                  {question.questionType === 'LONG_WRITING' && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Note:</span> Please note that, for the Writing exam, student responses may be considered correct even if they differ from the model answer. However, we recommend reviewing the model answer and accompanying explanation for further clarification.
                      </p>
                    </div>
                  )}
                  
                  {/* Show question image if available */}
                  {question.image && (
                    <div className="mb-3 relative group cursor-zoom-in inline-block max-w-full">
                      <img 
                        src={question.image} 
                        alt="Question" 
                        className="max-w-full h-auto rounded-lg border border-gray-200 transition-all hover:opacity-95 max-h-48 object-contain"
                        onClick={() => handleImageClick({
                          questionIndex: index,
                          questionNumber: index + 1,
                          image: question.image
                        })}
                      />
                      <div 
                        className="absolute bottom-2 right-2 bg-black/60 hover:bg-black/85 text-white p-2 rounded-full cursor-pointer transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageClick({
                            questionIndex: index,
                            questionNumber: index + 1,
                            image: question.image
                          });
                        }}
                      >
                        <ZoomIn className="h-4 w-4" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show options for MCQ and MULTI_SELECT questions */}
              {(question.questionType === 'MCQ' || question.questionType === 'MULTI_SELECT') && Array.isArray(question.options) && (
                <div className="ml-8 mb-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Options:</p>
                  <div className="space-y-2">
                    {question.options.map((option, optIndex) => {
                      const optionText = option.text || (typeof option === 'string' ? option : '');
                      const optionId = option.id || String.fromCharCode(65 + optIndex);
                      const isUserSelected = question.questionType === 'MCQ' 
                        ? question.userAnswer === optIndex
                        : Array.isArray(question.userAnswer) && question.userAnswer.includes(optionId);
                      const isCorrect = (() => {
                        const correctAnswers = Array.isArray(question.correctAnswer)
                          ? question.correctAnswer
                          : [question.correctAnswer];
                        
                        return correctAnswers.some(correctAns => {
                          if (correctAns === undefined || correctAns === null) return false;
                          const ansIdx = parseInt(correctAns);
                          if (!isNaN(ansIdx)) {
                            return ansIdx === optIndex;
                          }
                          return String(optionId) === String(correctAns) || 
                                 String(optionText) === String(correctAns) || 
                                 String(option) === String(correctAns);
                        });
                      })();
                      // Get answer image from answerImages array
                      const optionImage = question.answerImages && question.answerImages[optIndex] 
                        ? question.answerImages[optIndex] 
                        : null;
                      
                      return (
                        <div key={optIndex} className="flex items-center space-x-2 text-sm">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            isUserSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isUserSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                          </div>
                          <span className={`flex-1 leading-relaxed ${isCorrect ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                            {optionId}: {optionImage ? (
                              <img 
                                src={optionImage} 
                                alt={`Option ${optionId}`}
                                className="max-w-full h-auto rounded-lg max-h-20 object-contain inline-block ml-2 cursor-zoom-in hover:opacity-95 transition-opacity"
                                onClick={() => handleImageClick({
                                  questionIndex: index,
                                  questionNumber: index + 1,
                                  image: optionImage,
                                  title: `Question ${index + 1} - Option ${optionId}`
                                })}
                              />
                            ) : (
                              <span dangerouslySetInnerHTML={{ __html: optionText }} />
                            )}
                          </span>
                          {isCorrect && <CheckCircle className="h-4 w-4 text-green-600" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Show user answer and correct answer */}
              <div className="ml-8 space-y-2">
                <div className="text-sm">
                  <span className="text-gray-600">Your answer: </span>
                  <span className={`font-medium ${question.isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                    {question.questionType === 'SHORT_ANSWER' 
                      ? question.userAnswer || 'Not answered'
                      : question.questionType === 'LONG_WRITING'
                      ? (question.userAnswer ? (
                          <span className="text-[#782804]" dangerouslySetInnerHTML={{ __html: question.userAnswer }} />
                        ) : 'Not answered')
                      : question.questionType === 'MULTI_SELECT'
                      ? (() => {
                          const userAnswers = Array.isArray(question.userAnswer) ? question.userAnswer : [];
                          return userAnswers.map(ans => {
                            const index = parseInt(ans);
                            return isNaN(index) ? ans : String.fromCharCode(65 + index);
                          }).join(', ') || 'Not answered';
                        })()
                      : (question.userAnswer !== undefined && question.userAnswer !== null 
                        ? `${String.fromCharCode(65 + question.userAnswer)}`
                        : 'Not answered')
                    }
                  </span>
                </div>
                {/* Show model answer for LONG_WRITING */}
                {question.questionType === 'LONG_WRITING' && question.correctAnswer && (
                  <div className="text-sm">
                    <span className="text-gray-600">Model answer: </span>
                    <span 
                      className="font-medium text-green-600"
                      dangerouslySetInnerHTML={{ __html: question.correctAnswer }}
                    />
                  </div>
                )}
                
                {!question.isCorrect && question.questionType !== 'LONG_WRITING' && (
                  <div className="text-sm">
                    <span className="text-gray-600">Correct answer: </span>
                    <span className="font-medium text-green-600">
                      {question.questionType === 'SHORT_ANSWER'
                        ? (Array.isArray(question.correctAnswer) ? question.correctAnswer.join(', ') : question.correctAnswer)
                        : (question.questionType === 'MCQ' || question.questionType === 'MULTI_SELECT')
                        ? (() => {
                            const answers = Array.isArray(question.correctAnswer) 
                              ? question.correctAnswer 
                              : [question.correctAnswer];
                            return answers.map(ans => {
                              const index = parseInt(ans);
                              return isNaN(index) ? ans : String.fromCharCode(65 + index);
                            }).join(', ');
                          })()
                        : (Array.isArray(question.correctAnswer) ? question.correctAnswer.join(', ') : question.correctAnswer)
                      }
                    </span>
                  </div>
                )}
                
                {/* Show explanations if available */}
                {question.explanations && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Explanation:</p>
                    {typeof question.explanations === 'object' && !Array.isArray(question.explanations) ? (
                      <div className="space-y-1">
                        {Object.entries(question.explanations).map(([key, explanation]) => {
                          // Convert correctAnswer indices to letters for comparison
                          const correctAnswersAsLetters = Array.isArray(question.correctAnswer)
                            ? question.correctAnswer.map(ans => {
                                const index = parseInt(ans);
                                return isNaN(index) ? ans : String.fromCharCode(65 + index);
                              })
                            : [question.correctAnswer];
                          const isCorrectOption = correctAnswersAsLetters.includes(key);
                          
                          return (
                            <div key={key} className={`text-sm leading-relaxed ${isCorrectOption ? 'text-green-700' : 'text-red-700'}`}>
                              <span className={`font-medium ${isCorrectOption ? 'text-green-800' : 'text-red-800'}`}>
                                {key}:
                              </span> <span dangerouslySetInnerHTML={{ __html: explanation }} />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: question.explanations }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button
          onClick={() => navigate('/exams')}
          className="btn-primary flex-1"
        >
          Back to Exams
        </button>
        <button
          onClick={() => window.print()}
          className="btn-secondary flex-1"
        >
          Print Results
        </button>
      </div>

      {/* Debug: Raw API Data - Only show on localhost */}
      {window.location.hostname === 'localhost' && (
        <div className="mt-8 bg-gray-900 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-yellow-300">Debug: Raw API Data from Backend</h4>
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify({ examData, answers, questions }, null, 2));
                alert('Raw API data copied to clipboard!');
              }}
              className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded hover:bg-gray-600"
            >
              Copy to Clipboard
            </button>
          </div>
          <textarea
            readOnly
            value={JSON.stringify({ examData, answers, questions }, null, 2)}
            rows={15}
            className="w-full bg-gray-800 text-green-400 font-mono text-xs p-2 rounded border border-gray-700 resize-none"
          />
        </div>
      )}

      {/* Image Fullscreen Viewer */}
      {selectedImage && (
        <div
          ref={fullscreenRef}
          className="fixed inset-0 z-50 flex flex-col bg-black"
          style={{ cursor: isDragging ? 'grabbing' : 'default' }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-black/70 backdrop-blur-sm border-b border-white/10 shrink-0">
            <span className="text-white text-sm font-medium">
              {selectedImage.title || `Question ${selectedImage.questionNumber} of ${processedQuestions.length}`}
            </span>

            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 bg-white/10 rounded-lg px-2 py-1">
              <button
                onClick={() => setZoomScale(prev => Math.max(1, prev - 0.25))}
                disabled={zoomScale <= 1}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <span className="text-white text-xs font-mono w-12 text-center select-none">
                {Math.round(zoomScale * 100)}%
              </span>
              <button
                onClick={() => setZoomScale(prev => Math.min(5, prev + 0.25))}
                disabled={zoomScale >= 5}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
              <button
                onClick={() => { setZoomScale(1); setPan({ x: 0, y: 0 }); }}
                disabled={zoomScale === 1 && pan.x === 0 && pan.y === 0}
                className="text-white hover:text-gray-300 p-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-l border-white/20 pl-2 ml-1"
                title="Reset Zoom"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>

            {/* Right controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={closeModal}
                className="text-white hover:text-gray-300 p-2 transition-colors"
                title="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Image Canvas */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center"
            style={{ userSelect: 'none' }}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title || `Question ${selectedImage.questionNumber}`}
              draggable="false"
              className={`max-w-full max-h-full object-contain select-none ${
                zoomScale > 1 ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : ''
              }`}
              style={{
                transform: `scale(${zoomScale}) translate(${Math.round(pan.x / zoomScale)}px, ${Math.round(pan.y / zoomScale)}px)`,
                transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                imageRendering: zoomScale > 1 ? 'crisp-edges' : 'auto',
                WebkitImageRendering: zoomScale > 1 ? '-webkit-optimize-contrast' : 'auto',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onDoubleClick={handleDoubleClick}
            />
          </div>

          {/* Bottom hint */}
          <div className="text-center py-2 text-white/40 text-xs shrink-0">
            Double-click to toggle zoom · Drag to pan · Press Esc to close
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamResults; 